import React, {useCallback, useEffect} from "react";
import {makeFileVariable} from "@reside/ui";
import Alert from "react-s-alert";
import {pickBy} from "lodash";
import {FileService} from "../../services/FileService";
import {usePointClickCareStatus} from "../../hooks/usePointClickCareStatus";
import {BackdropSpinner} from "../../atoms/spinner";
import {Banner} from "../../atoms/banner";
import {ErrorBoundary} from "../../atoms/error-boundary";
import {FacilitySettingsForm} from "./atoms/facility-settings-form";
import {FacilitySettingsHeader} from "./atoms/facility-settings-header";
import {FacilitySettingsService} from "../../services/FacilitySettingsService";
import {useFacilitySettingsQuery} from "./hooks/useFacilitySettingsQuery";
import {UserDetail} from "../../store/users/usersModel";
import {useTemplateVersionQuery} from "../../hooks/useTemplateVersionQuery";
import {useFacilitySettingsTemplateQuery} from "../../hooks/useTemplateQuery";
import {useFacilityDocumentUploadCategoriesQuery} from "./hooks";
import {Facilities} from "../../models/types/AdmissionForm";
import {useFacilitySettingsParams} from "./hooks/useFacilitySettingsParams";
import {format, parse} from "date-fns";
import {useFetchFacilitiesQuery} from "../../routers/AdminAppRefresh";
import {transformV3FileUrl} from "../../utils/url";
import {getCommonAuthorizationHeader} from "../../services/api/selectors";

export const PageBody = ({
  user,
  facilities,
}: {
  user: UserDetail;
  facilities: Facilities;
}) => {
  const {facilityId} = useFacilitySettingsParams();
  const {fetchFacilitiesQuery} = useFetchFacilitiesQuery();

  const {
    isFetching: isFetchingVariables,
    error: fetchSettingsError,
    data: variables,
    remove: removeFacilitySettingsQuery,
  } = useFacilitySettingsQuery({
    facilityId,
  });

  const {isPccEnabled} = usePointClickCareStatus(facilities, facilityId);

  const {data: documentUploadCategory, isFetching: isLoadingPccCategories} =
    useFacilityDocumentUploadCategoriesQuery(facilities);

  const {data: {version} = {}} = useTemplateVersionQuery({
    facilityId,
  });

  const {
    data: template,
    isLoading: isLoadingTemplate,
    error: templateError,
  } = useFacilitySettingsTemplateQuery({
    versionId: version?.id,
  });

  useEffect(() => {
    return () => {
      removeFacilitySettingsQuery();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  const uploadFilesAndSaveSettings = useCallback(
    async values => {
      const files: Record<string, File> = pickBy(
        values,
        value => value instanceof File,
      );

      try {
        const fileReferences = await Promise.all(
          Object.entries(files).map(async ([variableName, file]) => {
            const {data: fileReference} = await FileService.upload(file, "VARIABLE", variableName);
            return [variableName, makeFileVariable(fileReference)];
          }),
        );

        const finalValues = {...values, ...Object.fromEntries(fileReferences)};

        try {
          await FacilitySettingsService.updateSettings(facilityId, finalValues);
          Alert.success("Facility Settings were successfully updated!");
          fetchFacilitiesQuery();
          return finalValues;
        } catch (error) {
          Alert.error(
            "Failed to upload the Facility Settings JSON. Please try again.",
          );
        }
      } catch (error) {
        Alert.error(
          error?.data?.message ??
            "Failed to upload some files in Facility Settings. Please try again.",
        );
      }

      return null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [facilityId],
  );

  /**
   * We pass key to Form to re-mount tree when we navigate again to /facility-settings.
   * Otherwise the <Prompt /> catches the navigation *twice* (because the dirty form is still rendered).
   */
  const HACK_PROMPT_KEY = facilityId;

  /**
   * Initial value for GO_LIVE_DATE formatted to MM/dd/yyyy.
   * The date from the database is in the format yyyy-MM-dd.
   * Normally, the date field formats this value, but here we display
   * the facility go-live date in a text field that cannot format dates on its own.
   *
   * @constant {string} FACILITY_GO_LIVE_DATE - The formatted facility go-live date, or an empty string if no date is provided.
   *
   */

  const FACILITY_GO_LIVE_DATE = variables?.facilityGoLiveDate
    ? format(
        parse(
          variables?.facilityGoLiveDate as string,
          "yyyy-MM-dd",
          new Date(),
        ),
        "MM/dd/yyyy",
      )
    : "";

  return (
    <BackdropSpinner
      active={
        isFetchingVariables || isLoadingTemplate || isLoadingPccCategories
      }
    >
      {templateError ? (
        <>
          <FacilitySettingsHeader />
          {templateError.status === 404 ? (
            <Banner title="Template not found">
              <p>Facility is missing the JSON template.</p>
            </Banner>
          ) : (
            <Banner title="Failed to get the Template">
              <p>There was error while fetching the template.</p>
              <p style={{overflow: "hidden"}}>{templateError.data?.message}</p>
            </Banner>
          )}
        </>
      ) : template && variables ? (
        <ErrorBoundary>
          <FacilitySettingsForm
            key={HACK_PROMPT_KEY}
            initialValues={{
              ...(variables as any),
              userRole: user.role.name,
              isPccEnabled: isPccEnabled as any,
              facilityGoLiveDate: FACILITY_GO_LIVE_DATE,
            }}
            header={<FacilitySettingsHeader />}
            template={template}
            templateNames={user.facilities.map(
              facility => facility.templateName,
            )}
            onSubmit={uploadFilesAndSaveSettings}
            isPccEnabled={isPccEnabled}
            queryVariables={{
              documentUploadCategory,
              transformFileUrl: transformV3FileUrl,
              getCommonAuthorizationHeader,
            }}
          />
        </ErrorBoundary>
      ) : (
        <>
          <FacilitySettingsHeader />
          {fetchSettingsError && (
            <Banner title="Failed to fetch the settings.">
              <p>There was error while fetching the Facility Settings.</p>
            </Banner>
          )}
        </>
      )}
    </BackdropSpinner>
  );
};
